<template>
  <div
    v-if="visibleItems.length"
    class="cti-card"
    :class="cardClass"
    data-component-id="chat--tool-timeline"
    role="status"
    aria-live="polite"
  >
    <!-- Header: status + total elapsed -->
    <div class="cti-header">
      <div class="cti-header-icon">
        <span class="cti-orb" :class="{ 'cti-orb-active': hasRunning }">
          <span class="cti-orb-dot"></span>
          <span class="cti-orb-ring"></span>
        </span>
      </div>
      <div class="cti-header-text">
        <div class="cti-header-title">
          <span class="cti-shimmer">{{ headerLabel }}</span>
        </div>
        <div class="cti-header-sub">
          <span>{{ doneCount }} / {{ visibleItems.length }} langkah</span>
          <span class="cti-header-sep">·</span>
          <span class="cti-header-elapsed">{{ totalElapsedLabel }}</span>
        </div>
      </div>
      <button
        v-if="visibleItems.length > 1"
        type="button"
        class="cti-toggle"
        :title="collapsed ? 'Lihat semua langkah' : 'Sembunyikan'"
        @click="collapsed = !collapsed"
      >
        <span class="cti-toggle-arrow" :class="{ 'is-open': !collapsed }">▾</span>
      </button>
    </div>

    <!-- Timeline list -->
    <transition-group
      v-if="!collapsed"
      name="cti-step"
      tag="ol"
      class="cti-list"
    >
      <li
        v-for="(item, i) in visibleItems"
        :key="item.key"
        class="cti-item"
        :class="[
          'cti-item--' + item.meta.category,
          { 'cti-item--running': item.status === 'running',
            'cti-item--done':    item.status === 'done',
            'cti-item--failed':  item.status === 'failed' }
        ]"
      >
        <div class="cti-rail">
          <span class="cti-rail-dot">
            <span v-if="item.status === 'running'" class="cti-spinner" aria-hidden="true"></span>
            <span v-else-if="item.status === 'done'" class="cti-check">✓</span>
            <span v-else-if="item.status === 'failed'" class="cti-fail">✕</span>
            <span v-else class="cti-pending">·</span>
          </span>
          <span v-if="i < visibleItems.length - 1" class="cti-rail-line"></span>
        </div>

        <div class="cti-body">
          <div class="cti-row">
            <span class="cti-icon" aria-hidden="true">{{ item.meta.icon }}</span>
            <span class="cti-label">{{ item.meta.label }}</span>
            <span class="cti-tool-name" :title="item.name">{{ item.name }}</span>
            <span class="cti-elapsed" :class="{ 'is-live': item.status === 'running' }">
              {{ formatElapsed(item.elapsed) }}
            </span>
          </div>
          <div v-if="item.summary" class="cti-summary">{{ item.summary }}</div>
          <div v-if="item.status === 'done' && item.preview" class="cti-preview" :title="item.preview">
            <span class="cti-preview-icon" aria-hidden="true">↳</span>{{ item.preview }}
          </div>
          <div v-if="item.status === 'done' && item.rowCount != null" class="cti-meta-pill">
            {{ item.rowCount }} baris hasil
          </div>
          <div v-if="item.status === 'failed'" class="cti-meta-pill cti-meta-pill--fail">
            tool gagal · AI akan beradaptasi
          </div>
        </div>
      </li>
    </transition-group>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getToolMeta, summarizeToolInput, formatElapsed } from '../data/chatToolMeta'

const props = defineProps({
  activity:    { type: Array,   required: true },
  isStreaming: { type: Boolean, default: false },
  nowTick:     { type: Number,  default: 0 }, // di-update tiap 250ms saat ada tool aktif
  // Mode kartu "Sumber data" per-pesan (pasca-streaming): label statis,
  // default collapse, dan layout embedded (tanpa indent rail kiri).
  label:            { type: String,  default: '' },
  defaultCollapsed: { type: Boolean, default: false },
  embedded:         { type: Boolean, default: false },
})

const collapsed = ref(props.defaultCollapsed)

// Re-collapse otomatis kalau jumlah tool tumbuh banyak (>4) supaya tidak
// terlalu memenuhi area chat. User tetap bisa expand manual.
watch(() => props.activity.length, (len) => {
  if (len > 4 && !collapsed.value) collapsed.value = true
  if (len <= 1 && collapsed.value) collapsed.value = false
})

const visibleItems = computed(() => {
  const now = props.nowTick || Date.now()
  return props.activity.map((t, idx) => {
    const meta    = getToolMeta(t.name)
    const summary = summarizeToolInput(t.name, t.input)
    let status = 'running'
    if (t.endedAt) {
      status = (t.ok === false) ? 'failed' : 'done'
    } else if (!props.isStreaming) {
      // Stream selesai tapi tool belum punya endedAt — anggap selesai
      status = (t.ok === false) ? 'failed' : 'done'
    }
    const start = t.startedAt || now
    const end   = t.endedAt || (status === 'running' ? now : start)
    return {
      key:      `${idx}-${t.name}-${t.startedAt || 0}`,
      name:     t.name || 'tool',
      meta,
      summary,
      status,
      ok:       t.ok,
      rowCount: t.rowCount,
      preview:  t.preview,
      elapsed:  Math.max(0, end - start),
    }
  })
})

const hasRunning = computed(() => visibleItems.value.some(i => i.status === 'running'))
const doneCount  = computed(() => visibleItems.value.filter(i => i.status !== 'running').length)

const totalElapsedLabel = computed(() => {
  const items = visibleItems.value
  if (!items.length) return ''
  const first = items[0]
  const last  = items[items.length - 1]
  const start = props.activity[0]?.startedAt || (props.nowTick || Date.now())
  const end = hasRunning.value
    ? (props.nowTick || Date.now())
    : (props.activity[props.activity.length - 1]?.endedAt || start)
  // Hindari unused-var warning di build
  void first; void last
  return formatElapsed(Math.max(0, end - start))
})

const headerLabel = computed(() => {
  if (props.label && !hasRunning.value) return props.label
  if (hasRunning.value) {
    const running = visibleItems.value.find(i => i.status === 'running')
    return running ? `${running.meta.label}…` : 'Yustian sedang menganalisis…'
  }
  if (visibleItems.value.some(i => i.status === 'failed')) {
    return 'Beberapa langkah gagal — AI menyesuaikan'
  }
  return 'Riset selesai · menyusun jawaban'
})

const cardClass = computed(() => ({
  'cti-card--running': hasRunning.value,
  'cti-card--done':    !hasRunning.value && !visibleItems.value.some(i => i.status === 'failed'),
  'cti-card--failed':  !hasRunning.value && visibleItems.value.some(i => i.status === 'failed'),
  'cti-card--embedded': props.embedded,
}))
</script>

<style scoped>
/* ── Card ─────────────────────────────────────────────────────────────────── */
/* ─── Card — premium glass + conic rotating border (running) ─────────────── */
@property --cti-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
/* Embedded (kartu "Sumber data" di dalam bubble): tanpa indent rail, lebar penuh.
   Selector 2-class agar menang spesifisitas atas .cti-card di bawah. */
.cti-card.cti-card--embedded {
  margin-left: 0;
  max-width: 100%;
  margin-top: 6px;
}
.cti-card {
  align-self: flex-start;
  margin-left: 39px;
  max-width: calc(100% - 50px);
  display: flex; flex-direction: column;
  border: 1.5px solid transparent;
  /* Glassy multi-stop bg: subtle accent tint at top, fade to card. */
  background:
    radial-gradient(
      120% 70% at 50% -10%,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 8%, transparent) 0%,
      transparent 55%
    ),
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 5%, var(--bg-card, #111827)),
      var(--bg-card, #111827) 70%
    );
  border-radius: 14px;
  padding: 10px 12px 4px;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.02) inset,
    0 4px 14px rgba(0, 0, 0, 0.28),
    0 0 22px color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent);
  position: relative;
  isolation: isolate;
  animation: cti-fade-in 0.25s ease;
}
/* Conic rotating border via mask-composite — only on running. */
.cti-card--running::before {
  content: '';
  position: absolute;
  inset: -1.5px;
  border-radius: inherit;
  padding: 1.5px;
  background: conic-gradient(
    from var(--cti-angle, 0deg),
    var(--accent-blue, #3b82f6) 0deg,
    var(--accent-purple, #8b5cf6) 90deg,
    var(--accent-blue-light, #7db4f5) 180deg,
    var(--accent-purple, #8b5cf6) 270deg,
    var(--accent-blue, #3b82f6) 360deg
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  animation: cti-rotate 5s linear infinite;
  z-index: -1;
  pointer-events: none;
}
.cti-card--running {
  animation: cti-fade-in 0.25s ease, cti-ambient 4.4s ease-in-out infinite;
}
/* Done & failed = static colored border (no animation). */
.cti-card--done {
  border-color: color-mix(in srgb, var(--accent-green, #22c55e) 38%, transparent);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.02) inset,
    0 4px 12px rgba(0, 0, 0, 0.28),
    0 0 18px color-mix(in srgb, var(--accent-green, #22c55e) 14%, transparent);
}
.cti-card--failed {
  border-color: color-mix(in srgb, var(--accent-red, #ef4444) 42%, transparent);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.02) inset,
    0 4px 12px rgba(0, 0, 0, 0.28),
    0 0 18px color-mix(in srgb, var(--accent-red, #ef4444) 14%, transparent);
}
/* Top inner gloss highlight for glass feel. */
.cti-card::after {
  content: '';
  position: absolute; inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.05) 0%,
    transparent 26%);
  z-index: 0;
}
.cti-card > * { position: relative; z-index: 1; }

@keyframes cti-rotate {
  to { --cti-angle: 360deg; }
}
@keyframes cti-ambient {
  0%, 100% {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.02) inset,
      0 4px 14px rgba(0, 0, 0, 0.28),
      0 0 22px color-mix(in srgb, var(--accent-blue, #3b82f6) 16%, transparent),
      0 0 38px color-mix(in srgb, var(--accent-purple, #8b5cf6) 10%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.04) inset,
      0 6px 18px rgba(0, 0, 0, 0.32),
      0 0 32px color-mix(in srgb, var(--accent-purple, #8b5cf6) 24%, transparent),
      0 0 56px color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent);
  }
}
@media (prefers-reduced-motion: reduce) {
  .cti-card--running { animation: cti-fade-in 0.25s ease; }
  .cti-card--running::before { animation-duration: 30s; }
}

@keyframes cti-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.cti-header {
  display: flex; align-items: center; gap: 10px;
  padding: 0 2px 6px;
}
.cti-header-icon { flex-shrink: 0; }
.cti-orb {
  position: relative;
  width: 22px; height: 22px;
  display: inline-flex; align-items: center; justify-content: center;
}
.cti-orb-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--accent-blue, #3b82f6);
  box-shadow: 0 0 10px var(--accent-blue, #3b82f6);
  z-index: 2;
}
.cti-orb-ring {
  position: absolute; inset: 0;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 60%, transparent);
  opacity: 0;
}
.cti-orb-active .cti-orb-ring { animation: cti-orb-ping 1.6s ease-out infinite; }
.cti-card--done .cti-orb-dot {
  background: var(--accent-green, #22c55e);
  box-shadow: 0 0 10px var(--accent-green, #22c55e);
}
.cti-card--failed .cti-orb-dot {
  background: var(--accent-red, #ef4444);
  box-shadow: 0 0 10px var(--accent-red, #ef4444);
}
@keyframes cti-orb-ping {
  0%   { transform: scale(0.6); opacity: 0.7; }
  80%, 100% { transform: scale(2); opacity: 0; }
}

.cti-header-text { flex: 1; min-width: 0; }
.cti-header-title {
  font-size: 12px; font-weight: 600; line-height: 1.3;
  color: var(--text-primary, #e2e8f0);
  display: flex; align-items: center; gap: 6px;
  letter-spacing: 0.1px;
}
.cti-shimmer {
  background: linear-gradient(
    90deg,
    var(--text-primary, #e2e8f0) 0%,
    var(--accent-blue-light, #7db4f5) 45%,
    var(--text-primary, #e2e8f0) 90%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
          background-clip: text;
  -webkit-text-fill-color: transparent;
}
.cti-card--running .cti-shimmer { animation: cti-shimmer 2.4s linear infinite; }
@keyframes cti-shimmer {
  from { background-position: 100% 0; }
  to   { background-position: -100% 0; }
}
.cti-header-sub {
  display: flex; align-items: center; gap: 5px;
  font-size: 10.5px; color: var(--text-muted, #94a3b8);
  margin-top: 1px;
}
.cti-header-sep { opacity: 0.5; }
.cti-header-elapsed { font-variant-numeric: tabular-nums; font-weight: 500; }

.cti-toggle {
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light, #1e2a3e));
  border-radius: 6px;
  padding: 2px 6px;
  cursor: pointer;
  color: var(--text-muted, #94a3b8);
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  font-size: 11px; line-height: 1;
}
.cti-toggle:hover {
  background: var(--bg-hover, rgba(125, 180, 245, 0.04));
  color: var(--text-primary, #e2e8f0);
  border-color: var(--accent-blue, #3b82f6);
}
.cti-toggle-arrow {
  display: inline-block;
  transition: transform 0.18s ease;
}
.cti-toggle-arrow.is-open { transform: rotate(180deg); }

/* ── Timeline list ────────────────────────────────────────────────────────── */
.cti-list {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column;
}
.cti-item {
  display: flex; gap: 8px;
  padding: 4px 2px 8px;
  position: relative;
}
.cti-item:last-child { padding-bottom: 4px; }

/* Rail (left timeline indicator) */
.cti-rail {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  flex-shrink: 0;
  width: 16px; padding-top: 2px;
}
.cti-rail-dot {
  width: 14px; height: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--bg-deep, #0a0e17);
  border: 1.5px solid var(--border-soft, var(--border-light, #1e2a3e));
  font-size: 9px; line-height: 1;
  flex-shrink: 0;
  z-index: 1;
  transition: border-color 0.2s, background 0.2s;
}
.cti-rail-line {
  flex: 1; width: 2px;
  margin-top: 2px; min-height: 12px;
  background: linear-gradient(
    180deg,
    var(--border-soft, var(--border-light, #1e2a3e)) 0%,
    color-mix(in srgb, var(--border-soft, #1e2a3e) 50%, transparent) 100%);
}

.cti-item--running .cti-rail-dot {
  border-color: var(--accent-blue, #3b82f6);
  background: color-mix(in srgb, var(--accent-blue, #3b82f6) 18%, var(--bg-deep, #0a0e17));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent);
}
.cti-item--done .cti-rail-dot {
  border-color: var(--accent-green, #22c55e);
  background: color-mix(in srgb, var(--accent-green, #22c55e) 22%, var(--bg-deep, #0a0e17));
  color: var(--accent-green, #22c55e);
  font-weight: 700;
}
.cti-item--failed .cti-rail-dot {
  border-color: var(--accent-red, #ef4444);
  background: color-mix(in srgb, var(--accent-red, #ef4444) 22%, var(--bg-deep, #0a0e17));
  color: var(--accent-red, #ef4444);
  font-weight: 700;
}

.cti-spinner {
  width: 9px; height: 9px; border-radius: 50%;
  border: 1.5px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 30%, transparent);
  border-top-color: var(--accent-blue, #3b82f6);
  animation: cti-spin 0.8s linear infinite;
}
@keyframes cti-spin { to { transform: rotate(360deg); } }
.cti-pending { color: var(--text-dim, #6b7a8d); font-weight: 700; }

/* Body */
.cti-body { flex: 1; min-width: 0; }
.cti-row {
  display: flex; align-items: center; gap: 7px;
  font-size: 11.5px; line-height: 1.4;
  color: var(--text-primary, #e2e8f0);
}
.cti-icon { font-size: 12.5px; line-height: 1; flex-shrink: 0; }
.cti-label {
  font-weight: 600;
  color: var(--text-primary, #e2e8f0);
}
.cti-tool-name {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 10.5px;
  color: var(--text-dim, #6b7a8d);
  background: color-mix(in srgb, var(--text-dim, #6b7a8d) 10%, transparent);
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 160px;
  flex-shrink: 1;
}
.cti-elapsed {
  margin-left: auto;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: var(--text-muted, #94a3b8);
  flex-shrink: 0;
  padding: 1px 6px; border-radius: 9px;
  background: var(--bg-deep, #0a0e17);
  border: 1px solid var(--border-soft, var(--border-light, #1e2a3e));
}
.cti-elapsed.is-live {
  color: var(--accent-blue-light, #7db4f5);
  border-color: color-mix(in srgb, var(--accent-blue, #3b82f6) 40%, transparent);
  animation: cti-elapsed-pulse 1.4s ease-in-out infinite;
}
@keyframes cti-elapsed-pulse {
  0%, 100% { opacity: 0.85; }
  50%      { opacity: 1; }
}
.cti-summary {
  margin-top: 2px;
  font-size: 11px; line-height: 1.45;
  color: var(--text-muted, #94a3b8);
  word-wrap: break-word; overflow-wrap: anywhere;
  font-style: italic;
}
/* Preview ringkas hasil tool (baris pertama bermakna) — sinyal "ada data nyata". */
.cti-preview {
  display: flex; align-items: baseline; gap: 5px;
  margin-top: 3px;
  font-size: 10.5px; line-height: 1.4;
  color: var(--text-base, var(--text-body, #c8d1dc));
  background: var(--bg-deep, #0a0e17);
  border: 1px solid var(--border-soft, var(--border-light, #1e2a3e));
  border-left: 2px solid color-mix(in srgb, var(--accent-green, #22c55e) 55%, transparent);
  border-radius: 5px;
  padding: 3px 8px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}
.cti-preview-icon { color: var(--accent-green, #22c55e); flex-shrink: 0; }
.cti-meta-pill {
  display: inline-flex; align-items: center;
  margin-top: 4px;
  padding: 1px 7px; border-radius: 9px;
  font-size: 10px; font-weight: 600; letter-spacing: 0.2px;
  background: color-mix(in srgb, var(--accent-green, #22c55e) 14%, transparent);
  color: var(--accent-green, #22c55e);
  border: 1px solid color-mix(in srgb, var(--accent-green, #22c55e) 30%, transparent);
}
.cti-meta-pill--fail {
  background: color-mix(in srgb, var(--accent-red, #ef4444) 12%, transparent);
  color: var(--accent-red, #ef4444);
  border-color: color-mix(in srgb, var(--accent-red, #ef4444) 32%, transparent);
}

/* ── Ads WRITE — aksi yang mengubah akun iklan LIVE: tandai amber agar menonjol ── */
.cti-item--ads-write .cti-label { color: var(--accent-yellow, #eab308); }
.cti-item--ads-write .cti-icon {
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--accent-yellow, #eab308) 55%, transparent));
}
.cti-item--ads-write .cti-tool-name {
  color: var(--accent-yellow, #eab308);
  background: color-mix(in srgb, var(--accent-yellow, #eab308) 12%, transparent);
}
.cti-item--ads-write .cti-summary { color: var(--accent-yellow, #eab308); font-style: normal; font-weight: 500; }

/* Step transitions — animate hanya opacity + transform supaya tidak ada
   cap max-height yang memotong konten step yang panjang. */
.cti-step-enter-active { transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1); }
.cti-step-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.cti-step-enter-from   { opacity: 0; transform: translateX(-8px); }
.cti-step-leave-to     { opacity: 0; transform: translateX(-4px); }

@media (max-width: 768px) {
  .cti-card {
    margin-left: 0;
    max-width: 100%;
  }
  .cti-row {
    flex-wrap: wrap;
    gap: 4px;
  }
  .cti-elapsed {
    margin-left: 0;
  }
  .cti-tool-name {
    max-width: 120px;
  }
}

@media (max-width: 480px) {
  .cti-card {
    padding: 8px 10px 4px;
    border-radius: 10px;
  }
  .cti-header-title {
    font-size: 11px;
  }
  .cti-header-sub {
    font-size: 9.5px;
  }
  .cti-row {
    font-size: 10.5px;
  }
  .cti-tool-name {
    max-width: 100px;
    font-size: 9.5px;
  }
  .cti-summary {
    font-size: 10px;
  }
  .cti-preview {
    font-size: 9.5px;
    padding: 2px 6px;
  }
}
</style>
